import { Alert, Col, FormCheck, FormGroup, FormLabel, Row } from "react-bootstrap";
import { Trans, useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import CustomerSelect from "../../../components/Custom/Main/Customer/CustomerSelect";
import PlanSelect from "../../../components/Custom/Subscription/Plan/PlanSelect";
import PlanDetails from "../../../components/Custom/Subscription/Plan/PlanDetails";
import EmployeeSelect from "../../../components/Custom/Main/Employee/EmployeeSelect";
import TextArea from "../../../components/Forms/Element/TextArea";
import FieldValidationError from "../../../components/Forms/Element/FieldValidationError";
import InputText from "../../../components/Forms/Element/InputText";
import { formatCardNumber, formatCardExpiry } from "../../charge/utils/ChargeUtils";
import CardBrands, { detectCardBrand } from "../../../components/Custom/Charge/CardBrands";
import CelcoinCard from "../../../components/Custom/Charge/CelcoinCard";
import AsteriskRequired from "../../../components/Common/AsteriskRequired";
import CustomSelect from "../../../components/Forms/Element/CustomSelect";
import FormSection from "../../../components/Common/Form/FormSection";
import HintCallout from "../../../components/Common/HintCallout";
import axiosInstance from "../../../../services/AxiosInstance";
import Api from "../../../../constants/Api";
import PaymentProvider from "../../../../constants/PaymentProvider";
import SegmentedButtons from "../../../components/Forms/Element/SegmentedButtons";
import PaymentMethodSelect from "../../../components/Custom/Common/PaymentMethod/PaymentMethodSelect";
import { OUTSIDE_PAYMENT_METHODS } from "../../../components/Custom/Charge/OutsidePaymentAction";
import "./SubscriberForm.css";

// Digitação do cartão no cadastro oculta: a tokenização da Celcoin está instável e o
// Asaas só tokeniza com liberação da conta. Quem quer pagar no balcão paga pelo próprio
// link, aberto pela recepção no modal que aparece ao salvar. Para reativar, volte para true.
export const DIRECT_CARD_ENTRY_ENABLED = false;

// Os três momentos da assinatura paga pelo link, na ordem em que acontecem. O tom segue a
// situação do contrato: âmbar enquanto espera o pagamento, verde quando passa a valer. Os
// textos ficam em subscription_link_flow_{key}_title e _text.
const PAYMENT_LINK_STEPS = [
    { key: 'pending', icon: 'fa-regular fa-clock', tone: 'is-pending' },
    { key: 'pay', icon: 'fa-solid fa-mobile-screen-button', tone: 'is-info' },
    { key: 'active', icon: 'fa-solid fa-circle-check', tone: 'is-active' },
];

const Form = (props) => {

    const { t } = useTranslation();
    const { formik, setPaymentToken, reset, onCustomerChange, entityData, setTokenizing, onOpenQuickCustomer } = props;

    // Detecta o provedor de pagamento do tenant para escolher o coletor de
    // cartão. Os dados sensíveis nunca são armazenados no Colmeia.
    const [currentProvider, setCurrentProvider] = useState(null);
    const [providerLoading, setProviderLoading] = useState(true);

    useEffect(() => {
        const loadCurrentProvider = async () => {
            try {
                // A empresa vem do JWT pelo endpoint já usado pela aplicação;
                // nunca inferimos tenant por localStorage ou por um ID padrão.
                const companyResponse = await axiosInstance.get(Api.registration.company_info);
                const tenantId = companyResponse?.data?.id;
                if (!tenantId) {
                    throw new Error('Empresa autenticada sem identificador.');
                }
                const response = await axiosInstance.get(`${Api.integration.payment}/provider/${tenantId}`);
                setCurrentProvider(response.data);
            } catch (error) {
                setCurrentProvider(null);
            } finally {
                setProviderLoading(false);
            }
        };

        loadCurrentProvider();
    }, []);

    useEffect(() => {
        // Tokens de cartão são específicos do provedor e devem ser descartados
        // quando o tenant muda ou o formulário é reiniciado.
        setPaymentToken(null);
    }, [currentProvider, setPaymentToken]);

    // Quem pode cobrar esta assinatura: o pacote da loja sempre; o provedor só quando o
    // tenant tem um configurado. É o backend que sabe (SubscriptionService#isProviderConfigured),
    // porque depende dos parâmetros do provedor e não só do provedor escolhido.
    const [billingOptions, setBillingOptions] = useState(null);

    useEffect(() => {
        axiosInstance.get(`${Api.subscription.subscription}/billing-options`)
            .then(({ data }) => setBillingOptions(data || null))
            // Sem a resposta, oferece só o pacote: o backend recusaria a contratação pelo
            // provedor de qualquer jeito se ele não estivesse configurado.
            .catch(() => setBillingOptions({ providerConfigured: false }));
    }, []);

    useEffect(() => {
        // Sem provedor configurado a única forma é o pacote da loja. Reaplicado no reset, que
        // devolve o formulário aos valores iniciais.
        if (billingOptions && !billingOptions.providerConfigured) {
            formik.setFieldValue('chargedAtStore', true, false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [billingOptions, reset]);

    // Plano com duração em dias é pacote da loja: o provedor só cobra em ciclos fixos. Ao escolher
    // um assim, a contratação vai para a loja e a opção do provedor fica travada com o motivo.
    const planIsDailyPackage = Number(formik.values.plan?.packageDays) > 0;
    useEffect(() => {
        if (planIsDailyPackage) {
            formik.setFieldValue('chargedAtStore', true, false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [planIsDailyPackage]);

    const storeBilling = Boolean(formik.values.chargedAtStore);
    const billingChoiceOptions = [
        {
            value: false,
            disabled: planIsDailyPackage,
            title: planIsDailyPackage ? 'Este plano tem duração em dias e só pode ser contratado como pacote na loja.' : undefined,
            label: t('subscription_billing_provider', { provider: billingOptions?.providerName || '' }),
            hint: t('subscription_billing_provider_hint'),
            icon: 'fa-solid fa-rotate'
        },
        {
            value: true,
            label: t('subscription_billing_store'),
            hint: t('subscription_billing_store_hint'),
            icon: 'fa-solid fa-store'
        }
    ];

    useEffect(() => {
        if (formik.values.providerBillingType !== 'CREDIT_CARD') {
            // Regra única do cadastro de assinaturas: todos os provedores usam crédito.
            formik.setFieldValue('providerBillingType', 'CREDIT_CARD');
            setPaymentToken(null);
        }

        // O Asaas agora usa o POST de assinatura com cartão, então o formulário coleta
        // os dados somente durante a contratação; eles não são armazenados no Colmeia.
        if (currentProvider === PaymentProvider.ASAAS) {
            setPaymentToken(null);
            setTokenizing?.(false);
        }
        // O reset alterna após salvar e precisa restaurar a regra fixa do provedor.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentProvider, reset]);
    const billingTypeOptions = [
        { value: 'CREDIT_CARD', label: t('credit_card'), icon: 'fa-regular fa-credit-card' },
        { value: 'DEBIT_CARD', label: t('debit_card'), icon: 'fa-solid fa-credit-card' },
        { value: 'PIX', label: t('pix'), icon: 'fa-brands fa-pix' },
        { value: 'BOLETO', label: t('banking_billet'), icon: 'fa-solid fa-barcode' }
    ];
    const effectiveBillingType = 'CREDIT_CARD';
    const selectedBillingType = billingTypeOptions.find((option) => option.value === effectiveBillingType) || billingTypeOptions[0];
    const syncStatusModifier = entityData == null
        ? 'is-info'
        : entityData.integrationId ? 'is-success' : 'is-warning';
    const syncStatusIcon = entityData == null
        ? 'fa-info-circle'
        : entityData.integrationId ? 'fa-check-circle' : 'fa-clock';

    const formatBillingTypeOption = (option) => (
        <div className="d-flex align-items-center gap-2">
            <i className={option.icon}></i>
            <span>{option.label}</span>
        </div>
    );

    return (
        <>
            <div className="subscription-subscriber-form form-layout form-layout--spaced">
                {/* Pacote da loja não sincroniza com provedor nenhum: o aviso só confundiria. */}
                {!storeBilling && (
                <div
                    className={`subscription-subscriber-form__sync-status ${syncStatusModifier}`}
                    role="status"
                >
                    <span className="subscription-subscriber-form__sync-status-icon" aria-hidden="true">
                        <i className={`fas ${syncStatusIcon}`}></i>
                    </span>
                    <div className="subscription-subscriber-form__sync-status-content">
                        <strong>
                            {entityData == null
                                ? t('sync_status')
                                : t(entityData.integrationId ? 'subscription_sync_synced' : 'subscription_sync_pending')}
                        </strong>
                        <div className="small mt-1 mb-0">
                            {entityData == null
                                ? t('subscription_sync_status_hint')
                                : t(entityData.integrationId ? 'subscription_sync_synced_hint' : 'subscription_sync_pending_hint')}
                        </div>
                    </div>
                </div>
                )}

                <FormSection
                    title={t('basic_information')}
                    description={t('subscription_subscriber_selection_hint')}
                    icon="fa-solid fa-user-check"
                >
                    <Row className="g-3">
                        <Col md={7}>
                            <FormGroup>
                                <div className="d-flex align-items-center justify-content-between gap-2 mb-1">
                                    <FormLabel className="mb-0">{t('customer')} <AsteriskRequired /></FormLabel>
                                    {onOpenQuickCustomer && (
                                        <button
                                            type="button"
                                            className="btn btn-link btn-sm p-0"
                                            onClick={onOpenQuickCustomer}
                                        >
                                            <i className="fas fa-plus me-1"></i>
                                            Novo cliente
                                        </button>
                                    )}
                                </div>
                                <CustomerSelect
                                    name="customer"
                                    value={formik.values.customer}
                                    crossBranch
                                    onChange={(value) => {
                                        if (onCustomerChange) {
                                            onCustomerChange(value);
                                        } else {
                                            formik.setFieldValue('customer', value);
                                        }
                                    }}
                                />
                                <FieldValidationError error={formik.errors.customer} />
                            </FormGroup>
                        </Col>
                        <Col md={5}>
                            <FormGroup>
                                <FormLabel>{t('plan')} <AsteriskRequired /></FormLabel>
                                <PlanSelect
                                    name="plan"
                                    value={formik.values.plan}
                                    onChange={(value) => {
                                        formik.setFieldValue('plan', value);
                                    }}
                                />
                                <FieldValidationError error={formik.errors.plan} />
                            </FormGroup>
                        </Col>
                        <Col md={7}>
                            <FormGroup>
                                <FormLabel>{t('subscription_sold_by')}</FormLabel>
                                {/* Opcional: nem toda assinatura nasce de uma venda com colaborador identificado (ex: integração). */}
                                <EmployeeSelect
                                    name="employee"
                                    value={formik.values.employee}
                                    onChange={(value) => formik.setFieldValue('employee', value)}
                                    filterProvidesService={false}
                                />
                                <FieldValidationError error={formik.errors.employee} />
                            </FormGroup>
                        </Col>
                    </Row>
                </FormSection>

                <FormSection
                    title={t('payment_information')}
                    description={t('subscription_subscriber_payment_hint')}
                    icon="fa-solid fa-credit-card"
                >
                    {/* A escolha só existe com provedor configurado: sem ele, o pacote é a única
                        forma e perguntar seria um clique sem decisão. Dois modelos convivem no
                        mesmo tenant, e quem cobra fica definido na contratação. */}
                    {billingOptions?.providerConfigured && (
                        <Row className="g-3 mb-3">
                            <Col md={12}>
                                <FormLabel>{t('subscription_billing_question')}</FormLabel>
                                <SegmentedButtons
                                    ariaLabel={t('subscription_billing_question')}
                                    value={storeBilling}
                                    // Sem validar na troca: escolher quem cobra não pode acender o
                                    // erro de cliente e plano antes de alguém tentar salvar.
                                    onChange={(value) => {
                                        formik.setFieldValue('chargedAtStore', value, false);
                                        formik.setFieldValue('storePaymentMethod', null, false);
                                    }}
                                    options={billingChoiceOptions}
                                />
                            </Col>
                        </Row>
                    )}

                    {/* Sem provedor o formulário já abre no modo pacote, sem pergunta. O aviso explica o
                        porquê e aponta o caminho para a recorrência: a configuração do provedor é do
                        administrador, então o operador pede à equipe em vez de procurar uma tela. */}
                    {billingOptions && !billingOptions.providerConfigured && (
                        <div className="subscription-subscriber-form__no-provider-note" role="status">
                            <i className="fa-solid fa-circle-info subscription-subscriber-form__no-provider-icon" aria-hidden="true"></i>
                            <div className="subscription-subscriber-form__no-provider-content">
                                <strong className="subscription-subscriber-form__no-provider-title">
                                    {t('subscription_no_provider_title')}
                                </strong>
                                <p className="subscription-subscriber-form__no-provider-body">
                                    {t('subscription_no_provider_body')}
                                </p>
                                <p className="subscription-subscriber-form__no-provider-action">
                                    <i className="fa-solid fa-headset" aria-hidden="true"></i>
                                    <span>
                                        <Trans i18nKey="subscription_no_provider_action" components={{ strong: <strong /> }} />
                                    </span>
                                </p>
                            </div>
                        </div>
                    )}

                    {storeBilling ? (
                        <>
                            <PlanDetails plan={formik.values.plan} />

                            {/* Mesmo aviso azul da nota do provedor, que ocupa este lugar quando a
                                cobrança é pelo Asaas. */}
                            <Alert variant="info" className="subscription-subscriber-form__provider-note">
                                <i className="fa-solid fa-store me-2"></i>
                                <Trans i18nKey="subscription_store_package_text" components={{ strong: <strong /> }} />
                            </Alert>

                            <Row className="g-3">
                                <Col md={6}>
                                    <FormGroup>
                                        <FormLabel>{t('outside_payment_how')} <AsteriskRequired /></FormLabel>
                                        <PaymentMethodSelect
                                            value={formik.values.storePaymentMethod}
                                            onChange={(value) => formik.setFieldValue('storePaymentMethod', value)}
                                            onlyMethods={OUTSIDE_PAYMENT_METHODS}
                                            error={formik.errors.storePaymentMethod}
                                        />
                                        <FieldValidationError error={formik.errors.storePaymentMethod} />
                                    </FormGroup>
                                </Col>
                            </Row>
                        </>
                    ) : (
                    <>
                    <Row className="g-3">
                        <Col md={4}>
                            <FormGroup>
                                <FormLabel>{t('payment_method')} <AsteriskRequired /></FormLabel>
                                <CustomSelect
                                    name="providerBillingType"
                                    value={selectedBillingType}
                                    options={billingTypeOptions}
                                    formatOptionLabel={formatBillingTypeOption}
                                    onChange={(option) => {
                                        const value = option?.value || 'CREDIT_CARD';
                                        formik.setFieldValue('providerBillingType', value);
                                        if (value !== 'CREDIT_CARD') {
                                            setPaymentToken(null);
                                        }
                                    }}
                                    onBlur={formik.handleBlur}
                                    placeholder={t('select')}
                                    isClearable={false}
                                    isDisabled={true}
                                    error={formik.errors.providerBillingType}
                                />
                            </FormGroup>
                        </Col>
                    </Row>

                    <PlanDetails plan={formik.values.plan} />

                    {/* Quem informa o cartão: a recepção agora ou o próprio cliente.
                        Muitos clientes têm receio de ditar o número do cartão no balcão, e
                        o link também tira a barbearia do caminho do dado sensível. */}
                    {/* No fluxo por link este aviso repetiria a explicação em passos logo
                        abaixo, que já diz que o cartão fica para as próximas mensalidades. */}
                    {currentProvider === PaymentProvider.ASAAS && !formik.values.paymentLinkRequested && (
                        <Alert variant="info" className="subscription-subscriber-form__provider-note mb-0">
                            <i className="fa-solid fa-credit-card me-2"></i>
                            Informe os dados do cartão para ativar a cobrança recorrente.
                        </Alert>
                    )}

                    {effectiveBillingType === 'CREDIT_CARD' && (
                        <div className="subscription-subscriber-form__payment-choice">
                            {DIRECT_CARD_ENTRY_ENABLED && (
                                <>
                                    <FormLabel>{t('subscription_card_entry_question')}</FormLabel>
                                    <div className="subscription-subscriber-form__payment-choice-options">
                                        <FormCheck
                                            type="radio"
                                            id="card-entry-now"
                                            name="cardEntry"
                                            label={t('subscription_card_entry_now')}
                                            checked={!formik.values.paymentLinkRequested}
                                            onChange={() => formik.setFieldValue('paymentLinkRequested', false)}
                                        />
                                        <FormCheck
                                            type="radio"
                                            id="card-entry-link"
                                            name="cardEntry"
                                            label={t('subscription_card_entry_link')}
                                            checked={Boolean(formik.values.paymentLinkRequested)}
                                            onChange={() => {
                                                // O token do cartão eventualmente digitado não vale
                                                // mais: quem vai pagar é o cliente, pelo link.
                                                setPaymentToken(null);
                                                setTokenizing?.(false);
                                                formik.setFieldValue('paymentLinkRequested', true);
                                            }}
                                        />
                                    </div>
                                </>
                            )}
                            {formik.values.paymentLinkRequested ? (
                                // Hoje é o único caminho de cadastro, então a explicação precisa
                                // responder sozinha o que a recepção pergunta depois: por que a
                                // assinatura não ficou ativa e onde achar o link de novo.
                                <div className="subscription-subscriber-form__link-flow">
                                    <div className="subscription-subscriber-form__link-flow-header">
                                        <span className="subscription-subscriber-form__link-flow-header-icon" aria-hidden="true">
                                            <i className="fa-solid fa-link"></i>
                                        </span>
                                        <div>
                                            <strong>{t('subscription_link_flow_title')}</strong>
                                            <p>{t('subscription_link_flow_subtitle')}</p>
                                        </div>
                                    </div>

                                    <ol className="subscription-subscriber-form__link-steps">
                                        {PAYMENT_LINK_STEPS.map((step, index) => (
                                            <li key={step.key} className={`subscription-subscriber-form__link-step ${step.tone}`}>
                                                <div className="subscription-subscriber-form__link-step-head">
                                                    <span className="subscription-subscriber-form__link-step-icon" aria-hidden="true">
                                                        <i className={step.icon}></i>
                                                    </span>
                                                    <span className="subscription-subscriber-form__link-step-number">
                                                        {t('subscription_link_flow_step', { number: index + 1 })}
                                                    </span>
                                                </div>
                                                <strong>{t(`subscription_link_flow_${step.key}_title`)}</strong>
                                                <span className="subscription-subscriber-form__link-step-text">
                                                    {/* Só o link do Asaas é recorrente: o cartão informado nele
                                                        fica no contrato. Nos demais provedores não há garantia
                                                        disso, então o texto não promete. */}
                                                    {step.key === 'pay' && currentProvider === PaymentProvider.ASAAS
                                                        ? t('subscription_link_flow_pay_text_recurring')
                                                        : t(`subscription_link_flow_${step.key}_text`)}
                                                </span>
                                            </li>
                                        ))}
                                    </ol>

                                    <HintCallout icon="fa-paper-plane">
                                        <Trans i18nKey="subscription_link_flow_tip" components={{ strong: <strong /> }} />
                                    </HintCallout>
                                </div>
                            ) : (
                                <small className="subscription-subscriber-form__payment-choice-hint">
                                    {t('subscription_card_entry_now_hint')}
                                </small>
                            )}
                        </div>
                    )}

                    {effectiveBillingType === 'CREDIT_CARD' && currentProvider === PaymentProvider.ASAAS
                            && !formik.values.paymentLinkRequested && (
                        <Row className="g-3 subscription-subscriber-form__asaas-card">
                            <Col md={12}><CardBrands brand={detectCardBrand(formik.values.creditCard?.number || '')} /></Col>
                            <Col md={12}><FormGroup><InputText placeholder={t('payment_card_number')} name="creditCard.number" value={formik.values.creditCard?.number || ''} onChange={(event) => formik.setFieldValue('creditCard.number', formatCardNumber(event.target.value))} inputMode="numeric" maxLength={19} autoComplete="cc-number" /></FormGroup></Col>
                            <Col md={12}><FormGroup><InputText placeholder={t('payment_card_holder')} name="creditCard.holderName" value={formik.values.creditCard?.holderName || ''} onChange={formik.handleChange} autoComplete="cc-name" /></FormGroup></Col>
                            <Col xl={6}><FormGroup><InputText placeholder={t('payment_card_expiry')} name="creditCard.expiry" value={formik.values.creditCard?.expiry || ''} onChange={(event) => { const formatted = formatCardExpiry(event.target.value); const digits = formatted.replace(/\D/g, ''); formik.setFieldValue('creditCard.expiry', formatted); formik.setFieldValue('creditCard.expiryMonth', digits.slice(0, 2)); formik.setFieldValue('creditCard.expiryYear', digits.slice(2) ? `20${digits.slice(2)}` : ''); }} maxLength={5} autoComplete="cc-exp" /></FormGroup></Col>
                            <Col xl={6}><div className="input-group"><InputText placeholder={t('payment_card_cvv')} name="creditCard.ccv" value={formik.values.creditCard?.ccv || ''} onChange={formik.handleChange} inputMode="numeric" maxLength={4} autoComplete="cc-csc" /><span className="input-group-text"><i className="fa-solid fa-credit-card"></i></span></div></Col>
                        </Row>
                    )}

                    {effectiveBillingType === 'CREDIT_CARD' && currentProvider !== PaymentProvider.ASAAS
                            && !formik.values.paymentLinkRequested && (
                        <div className="subscription-subscriber-form__card-payment">
                            {providerLoading ? (
                                <Alert variant="info" className="mb-0">
                                    <i className="fa-solid fa-spinner fa-spin me-2"></i>
                                    {t('loading')}
                                </Alert>
                            ) : currentProvider === PaymentProvider.CELCOIN ? (
                                <CelcoinCard
                                    setPaymentToken={setPaymentToken}
                                    setTokenizing={setTokenizing}
                                    reset={reset}
                                />
                            ) : (
                                <Alert variant="danger" className="mb-0">
                                    {t('validation.custom.payment_provider_not_configured')}
                                </Alert>
                            )}
                        </div>
                    )}
                    </>
                    )}
                </FormSection>

                <FormSection
                    title={t('notes')}
                    description={t('subscription_subscriber_notes_hint')}
                    icon="fa-solid fa-note-sticky"
                >
                    <Row>
                        <Col md={12}>
                            <FormGroup>
                                <FormLabel>{t('obs')}</FormLabel>
                                <TextArea
                                    name="obs"
                                    style={{ height: "100px" }}
                                    maxLength={255}
                                    value={formik.values.obs}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    counter="true"
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                </FormSection>
            </div>
        </>
    );
}

export default Form;
